import {
  useId,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
  type FormEvent,
  type ReactElement,
} from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  STICKER_UPLOAD_MAX_BYTES,
  clampFrontStickerPlacement,
  type ApiError,
  type BoardFaceList,
  type PlacedSticker,
  type StickerLibraryRow,
} from '@molt/shared';
import { Upload } from 'lucide-react';

import { Button, Notice, TextField } from '@/components';
import { announce } from '@/lib/announce';
import { isOptimisticCardId } from '@/lib/cards';
import { cn } from '@/lib/cn';
import { SETTLED, cardHeight } from '@/lib/density';
import { useInterfaceLanguage } from '@/lib/interface-language';
import { queryKeys } from '@/lib/query-keys';
import {
  STICKER_UPLOAD_ACCEPT,
  isStickerUploadContentType,
  placeSticker,
  stickerImageSrc,
  stickerLabel,
  useBoardStickers,
  useUploadCustomSticker,
  type StickerTranslate,
} from '@/lib/stickers';
import { STICKER_DRAG_MEDIA_TYPE } from './SortableCardTile';

/**
 * `FE-58.1` / `FR-CARD-032` AC1 / `FR-EXP-027` AC1 — the board-menu sticker
 * tray, and the **drag source** whose absence is the whole of the residual:
 * *"given the sticker tray is open, when the user drags a sticker onto a card
 * front"* — the tray and the card-front drop target did not exist, and
 * placement happened only from the card back's picker.
 *
 * ---------------------------------------------------------------------------
 * WHERE IT SITS, AND WHY IT COSTS THE BOARD FACE NOTHING
 * ---------------------------------------------------------------------------
 * Both requirements put the tray **in the board menu** in their own behaviour
 * text, not in persistent board-face chrome, and `AUDIT.md` §8 prices that at
 * **0px** against a 2.4px density headroom (`docs/notes/density-budget.md`, 24
 * cards at 1440×900): the panel is already mounted and already scrolls, so
 * nothing new is drawn on the board itself. That is the product tie-breaker
 * applied — *a board must stay readable in ten seconds, and where a feature
 * makes the board harder to read at a glance, the feature loses* — rather than
 * a layout convenience.
 *
 * ---------------------------------------------------------------------------
 * ⚠ THE DRAG IS NATIVE HTML5, NOT dnd-kit, AND THAT IS A FINDING RATHER THAN A
 * SHORTCUT
 * ---------------------------------------------------------------------------
 * The board face already carries **two** dnd-kit drags — a card between lists
 * and a list along the strip — through one `DndContext`. A third dnd-kit drag
 * typed `'sticker'` would fail `isCardDrag` in `lib/card-drag.ts`'s
 * `boardCollisionDetection` and be routed into its **list** branch, where every
 * card droppable has already been filtered out; a sticker could then never
 * resolve a card as its target and would silently drop on a column instead
 * (`AUDIT.md` §1.2). `card-drag.ts` is not this chunk's file, so the sticker
 * drag is native — which is also in-house precedent rather than an invention:
 * `PlannerPage.tsx`, `CardChecklistSection.tsx`, `BoardTimelineView.tsx`,
 * `BoardCalendarView.tsx` and `BoardFace.tsx`'s own pinned-view reorder all run
 * native `draggable`/`dragover`/`drop`.
 *
 * **The three drags cannot observe one another, structurally.** dnd-kit's
 * `PointerSensor` activates on `pointerdown` against an activator node, and a
 * drag begun on a tray tile never fires `pointerdown` there; dnd-kit registers
 * no `dragstart`/`dragover`/`drop` listener at all, and the native drag
 * protocol fires no pointer events once a drag is under way. No `DragOverlay`
 * participates in the sticker drag — the browser paints the native drag image —
 * so the recorded `rotate-2` defect (a CSS transform on a `DragOverlay`'s
 * outermost child, which made a card impossible to drag between lists by
 * keyboard with nothing red anywhere) **cannot apply to this gesture**, and
 * nothing here adds a transform to anything.
 *
 * ---------------------------------------------------------------------------
 * ⚠ THE WIRE FORMAT IS `SortableCardTile.tsx`'s, IMPORTED AND NEVER RE-DERIVED
 * ---------------------------------------------------------------------------
 * `STICKER_DRAG_MEDIA_TYPE` is declared in the **drop target's** file and read
 * here. Two string literals that were meant to be equal are exactly the defect
 * `tsc` cannot see: a second `'application/x-molt-sticker'` spelled anywhere
 * else drifts on the first rename, the drop target's
 * `types.includes(…)` guard then never matches, `dragover` never calls
 * `preventDefault()`, the browser's default action for `dragover` is *reject* —
 * and **the `drop` event never fires at all**, with both unit suites green.
 *
 * The payload is the **bare sticker id** in both channels, on `PlannerPage`'s
 * shape (`setData(PLANNER_CARD_DRAG_TYPE, id)` then `setData('text/plain',
 * id)`): the custom type is the discriminator, `text/plain` is the fallback a
 * reader can always reach. No name and no JSON travels — the drop's `POST`
 * answers with a whole `PlacedSticker`, so `stickerLabel(t, placed.sticker)`
 * recovers every string a later announcement needs.
 *
 * ---------------------------------------------------------------------------
 * PRINCIPLE 5 — THE KEYBOARD PATH IS THE SAME PLACEMENT, NOT A LESSER ONE
 * ---------------------------------------------------------------------------
 * A sighted-only placement fails `FR-EXP-027` even when the mouse path is
 * perfect. So the tray offers **choose a sticker, choose a card, place**: the
 * library tiles are toggles, and selecting one reveals every placeable card on
 * the board, grouped under its list, each button carrying the whole
 * `cardBack.stickers.placeLabel` message (*"Place sticker {sticker} on
 * {title}"*) as its accessible name. That path issues the same
 * `POST /cards/:id/stickers` the pointer drop ends in, with the same
 * `surface: 'front'`, and announces through the **existing** live region
 * (`lib/announce.ts`) rather than mounting a second one — `DndContext` already
 * renders one of its own for the card and list drags, and this gesture does not
 * go through it.
 *
 * ---------------------------------------------------------------------------
 * `FE-58.1` RESIDUAL 2 — THE TRAY'S UPLOAD CONTROL, AN ENTRY POINT ONTO A
 * PIPELINE THAT ALREADY SHIPS
 * ---------------------------------------------------------------------------
 * `FR-CARD-032` AC3's precondition is literally *"given the sticker tray is
 * open, when a member uploads a custom image"*, and until this chunk the tray
 * held **0 occurrences of `type="file"`** — the only upload path in the
 * product was the card back's picker, which is a different surface reached a
 * different way. What was missing was the control, never the plumbing:
 * `useUploadCustomSticker` drives presign → PUT → confirm end to end
 * (`POST /boards/:id/stickers/presign` → `PUT` to object storage →
 * `POST /boards/:id/stickers`), and this file adds no request, no route and no
 * byte of transport.
 *
 * ⚠ **THE FORM IS OUTSIDE THE LIBRARY'S THREE-ARMED TERNARY, AND THAT IS THE
 * WHOLE POINT OF IT.** The built-in sticker pack has no data anywhere — 0
 * sticker rows in `molt_v1`, and every one of `molt_v1_probe`'s 344 is
 * `is_custom = true` — so an ordinary board renders `libraryEmpty` and nothing
 * else. Rendering the upload form only beside a non-empty grid would leave the
 * empty board with **no path to a sticker at all**, which is residual 2 exactly
 * as it stood. It is the empty arm that needs it most.
 *
 * ⚠ **THE GATE IS `canPlace`, MEASURED AGAINST THE ROUTE RATHER THAN ASSUMED.**
 * Both upload legs call `requireBoardWriter` then `assertBoardIsOpen`
 * (`routes/stickers.ts:387–388`, `:419–420`) — byte-identical to what the
 * placement leg demands — and `BoardMenuPanel` derives `canPlaceStickers` as
 * `rolesKnown && !board.closed && role !== null && role !== 'observer'`, which
 * is that pair. So one prop gates both halves honestly, and the **whole block
 * is omitted** for a viewer who may not place rather than rendered disabled: a
 * second refused control would repeat the one `disabledReason` sentence a third
 * time in a panel that already says it twice, for a member who cannot act on
 * either half.
 *
 * ⚠ **THE SIZE AND TYPE CHECK RUNS HERE, BEFORE `mutateAsync`, AND IT IS NOT A
 * THIRD COPY OF A RULE.** `useUploadCustomSticker`'s `mutationFn` guards on the
 * same two shared constants and throws the same two routed messages — but its
 * `onError` fires `announce('Could not upload the sticker.')`, which is a
 * report about a *failed upload*. A 2 MiB PNG never reaches the network, so
 * announcing a failure for it would describe something that did not happen. The
 * decision itself lives in `STICKER_UPLOAD_MAX_BYTES` and
 * `isStickerUploadContentType`, both imported; only the moment of asking is
 * local, and asking early is what keeps the submit button honestly disabled.
 *
 * ⚠ **ONE SINK FOR THE SUCCESS SENTENCE — `announce()` AND NOTHING ELSE.** The
 * catalogue's own comment beside `cardBack.stickers.upload.complete` records
 * that the card back pairs it with a visible `role="status"` paragraph and that
 * `role="status"` *is* a live region, so a screen reader hears the sentence
 * twice; that is a recorded finding, and repeating it here would propagate it
 * to a second surface. The visible confirmation costs no copy at all:
 * `useUploadCustomSticker`'s `onSuccess` writes the new row into
 * `queryKeys.boards.stickers(boardId)`, so **the tile appears in the grid
 * above** — immediately draggable and immediately choosable by keyboard, which
 * is the entry point actually closing rather than a sentence claiming it did.
 *
 * ---------------------------------------------------------------------------
 * ⚠ ZERO NEW CATALOGUE KEYS, AND THE SIBLING TEST PROVES IT MECHANICALLY
 * ---------------------------------------------------------------------------
 * Every string this file routes is a key `B58` already declared for the card
 * back — `loadingLibrary`, `libraryEmpty`, `placeLabel`, `announce.added`,
 * `announce.placeFailed`, `untitledCard`, and for the upload block
 * `upload.fileLabel`, `upload.nameLabel`, `upload.submit`, `upload.busy`,
 * `upload.complete`, `upload.tooLarge`, `upload.unsupportedType` and
 * `error.uploadFailed` — plus user data (a sticker's name, a list's title, a
 * card's title) and server-authored error messages, which are not copy.
 *
 * ⚠ **The `<form>` is deliberately UNNAMED, and `upload.formLabel` is
 * deliberately not reached for.** That key is *"Custom sticker upload for
 * {title}"* and `interfaceMessage` THROWS on a slot with no value; the tray has
 * no card, so there is no title to give it, and inventing one would be a second
 * prop on `BoardMenuPanel` for a string. Naming the form with
 * `upload.fileLabel` instead — the only shipped slotless candidate — would put
 * that one string on two elements and make `getByLabelText` ambiguous for every
 * later reader, unit or browser. An unnamed `<form>` is a generic element that
 * still submits on Enter; the file input carries the name, and the submit
 * button carries the verb. `interface-language.tsx` is a 3,821-key append-only registry that
 * every chunk of this fan-out edits, and the cheapest outcome of that rule is
 * to add nothing to it. `BoardStickerTray.test.tsx`'s *"declares every key it
 * routes"* sweep is what turns that from a claim into a measurement: `t`
 * returns the KEY STRING for an undeclared key, so a missing declaration
 * renders as a plausible-looking `cardBack.stickers.whatever` that only an
 * attribute-inclusive scan of the real catalogue catches.
 */
export interface BoardStickerTrayProps {
  /**
   * Reads `GET /boards/:id/stickers` (the library) and writes
   * `POST /cards/:id/stickers` (a placement). Both routes shipped with B58;
   * this chunk writes neither a route nor a migration nor an event —
   * `sticker.placed` is already in the live CHECK on `activity_entries` and
   * already carries `board_id` on a column, so the socket fan-out is already
   * correct and `FR-EXP-027` AC1's *"other board members see it without a
   * reload"* rides existing wiring.
   */
  boardId: string;
  /**
   * Every list with its cards — the same array the board face and the email
   * destination picker already receive, so the keyboard path can name a card
   * without a second query.
   */
  lists: readonly BoardFaceList[];
  /**
   * The editor gate. **Reading the library is ungated and placing is not**:
   * `GET /boards/:id/stickers` resolves `currentUser(request)?.id ?? null`
   * rather than `requireUser`, so it answers 200 to an anonymous reader and 200
   * on a closed board, while `POST /cards/:id/stickers` is 404-then-403-then-409.
   */
  canPlace: boolean;
  /** The sentence beside a refused control; `null` when the tray may be used. */
  disabledReason: string | null;
}

/**
 * Where a KEYBOARD placement lands on a card front, in the card's own pixels.
 *
 * ⚠ **This is the second copy of one decision, and collapsing the two is a
 * named residual rather than an oversight.** The first is
 * `CardStickerSection.tsx`'s module-private `SURFACE_DROP_POINT.front`, which
 * this chunk may not widen into an export because that file is being changed by
 * another agent in the same fan-out. The number is *derived* there and the
 * derivation is repeated here so this file can be read on its own:
 * `lib/density.ts` puts a list column at 292px and `ListPanel`'s padding at 8px
 * a side, so a card front is about **276 × 107** with a left-aligned title at
 * the top and `CardBadges`' strip along the bottom — and the **top-right corner
 * is the only region that is neither**. `FR-CARD-032`'s own edge case is that a
 * sticker *"must not obscure the title to the point of illegibility"*, and the
 * default that never lands on the title is how that is honoured without
 * inventing a contrast number no client-side rule could hold against an
 * arbitrary uploaded image.
 *
 * ⚠ **180 rather than a computed right edge (244), and the 52px matters.**
 * `CardStickerSection`'s nudge controls clamp a placement to 192, so a tray
 * placement at 244 would snap *backwards* 52px on the member's first press from
 * the card back. Two front-placement paths that disagree about a coordinate is
 * precisely the drift the shared `placedStickerSchema` element exists to
 * prevent.
 */
const KEYBOARD_DROP_POINT = { x: 180, y: 8 } as const;

/** `--spacing-list` (292) less `ListPanel`'s `p-2` on each side. */
const REFERENCE_TILE_WIDTH = SETTLED.columnWidth - SETTLED.panelPadding * 2;

/**
 * The tallest of the three card fronts a real board contains — 107.2px.
 * `cardHeight` is `FE-04.12`'s own arithmetic, so this file inherits the
 * density budget rather than restating a number beside it.
 */
const REFERENCE_TILE_HEIGHT = cardHeight('labelled');

type BoardFaceCardSurface = BoardFaceList['cards'][number];

/**
 * ⚠ **PARITY WITH THE POINTER PATH, AND IT MUST NOT DRIFT — THE GATE IS
 * `canAct`, NOT `canEditOrdinaryFields`.**
 *
 * `SortableCardTile` re-gates its own drop rather than trusting the composer,
 * and the gate it uses is `canEdit && !isPlaceholder && !isMirrorSurface` —
 * the one `onArchive` uses. Two cards are refused there and are refused here:
 * an **optimistic** card has no server id for a placement to point at, and a
 * **mirror** is a read-only projection of a card that lives on another board.
 *
 * ⚠ **A link card is NOT refused, and that is measured rather than assumed.**
 * `CardTile` computes its front sticker layer from `card.stickers`
 * unconditionally, so a link card already *displays* placed stickers; refusing
 * to *receive* one would leave a surface that shows stickers and silently
 * declines them, which a member experiences as a dead drop with no
 * explanation and which no requirement asks for. This file tracked the
 * narrower gate for one commit and it was wrong in that direction.
 *
 * The two paths have to agree in both directions: a keyboard chooser that
 * offered a mirror would reach a placement the mouse cannot make and answer
 * 404 from the route, and one that withheld a link card would make
 * `FR-EXP-027`'s keyboard equivalent strictly weaker than the drag it stands
 * in for.
 */
function acceptsFrontSticker(card: BoardFaceCardSurface): boolean {
  const surfaceType = 'surfaceType' in card ? card.surfaceType : 'card';
  return surfaceType !== 'mirror' && !isOptimisticCardId(card.id);
}

/**
 * The one refusal the tray can decide without asking the network, and the two
 * shared constants it is decided from.
 *
 * ⚠ **MODULE SCOPE, so it cannot read `t` from context** — the translator is a
 * required leading parameter for the same reason every `lib/stickers.ts` export
 * takes one: the context resolves against an English fallback catalogue when no
 * provider is mounted, so a helper that read it itself would render English
 * with nothing red.
 *
 * ⚠ **It is not a copy of a rule, it is a second ASKING of one.** Both branches
 * delegate to `STICKER_UPLOAD_MAX_BYTES` and `isStickerUploadContentType`,
 * which are also what `useUploadCustomSticker`'s `mutationFn` and the route's
 * own `stickerPresignBodySchema` are built from — one decision, three readers,
 * and no literal repeated anywhere. Asking here rather than letting the
 * mutation throw is what keeps the announcement truthful: the hook's `onError`
 * says the upload failed, and a file that never left the browser did not fail
 * to upload.
 */
function stickerUploadRefusal(t: StickerTranslate, file: File): string | null {
  if (file.size > STICKER_UPLOAD_MAX_BYTES) {
    return t('cardBack.stickers.upload.tooLarge');
  }
  if (!isStickerUploadContentType(file.type)) {
    return t('cardBack.stickers.upload.unsupportedType');
  }
  return null;
}

interface TrayPlacement {
  readonly cardId: string;
  readonly stickerId: string;
  readonly stickerName: string;
  readonly positionX: number;
  readonly positionY: number;
  readonly zIndex: number;
}

export function BoardStickerTray({
  boardId,
  lists,
  canPlace,
  disabledReason,
}: BoardStickerTrayProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const queryClient = useQueryClient();
  const fileInputId = useId();
  /*
   * ⚠ The ref exists for ONE job — clearing the native input after a successful
   * upload. `<input type="file">` is uncontrolled by construction (its `value`
   * may only be set to `''`), so leaving it alone would show the member the
   * name of a file they have already uploaded, beside a submit button that is
   * disabled because state says nothing is chosen.
   */
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedStickerId, setSelectedStickerId] = useState<string | null>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadName, setUploadName] = useState('');
  const [uploadError, setUploadError] = useState<string | null>(null);
  /*
   * Mount IS the query gate: `BoardMenuPanel` renders this only while
   * `section === 'stickers'`, so an `enabled` prop would be a second switch
   * over the same fact. `useBoardStickers` announces nothing and keeps its
   * two-argument shape — the other four `lib/stickers.ts` hooks take the
   * translator first.
   */
  const stickers = useBoardStickers(boardId, true);
  /*
   * `FE-58.1` residual 2 — presign → PUT → confirm, whole, in the hook that
   * already owns it. Its `onSuccess` prepends the confirmed row into
   * `queryKeys.boards.stickers(boardId)`, which is the same key
   * `useBoardStickers` reads above, so an uploaded sticker appears in the grid
   * of this very component with no second fetch and no refetch flicker.
   *
   * ⚠ `t` first and required, never `useInterfaceLanguage()` inside the hook:
   * `lib/stickers.ts` states the reason beside `StickerTranslate`, and this is
   * the seventh call site `tsc` enumerates because of it.
   */
  const uploadSticker = useUploadCustomSticker(t, boardId);

  /**
   * ⚠ A LOCAL `useMutation` OVER THE PLAIN EXPORT, not `usePlaceSticker`, and
   * the reason is structural: `usePlaceSticker(t, boardId, cardId)` binds
   * `cardId` **at hook construction**, which is useless to a tray that may
   * place on any of 24 cards on the board. React forbids calling it per card.
   *
   * ⚠ It invalidates **both** caches the shipped hook maintains. Dropping the
   * card-detail key would leave an open card back showing a stale sticker list
   * after a tray placement — one surface right and one wrong, which is the
   * disagreement `placedStickerSchema` is shared to prevent. `invalidateQueries`
   * on both rather than `setQueryData` on the detail: this file does not own
   * `CardDetail`'s shape and must not write it.
   */
  const placement = useMutation<PlacedSticker, ApiError, TrayPlacement>({
    mutationFn: (variables) =>
      placeSticker(variables.cardId, {
        stickerId: variables.stickerId,
        surface: 'front',
        positionX: variables.positionX,
        positionY: variables.positionY,
        rotation: 0,
        zIndex: variables.zIndex,
      }),
    onSuccess: (_placed, variables) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.contents(boardId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.cards.detail(variables.cardId),
      });
      announce(t('cardBack.stickers.announce.added', { sticker: variables.stickerName }));
    },
    onError: () => announce(t('cardBack.stickers.announce.placeFailed')),
  });

  const library = stickers.data ?? [];
  const selectedSticker: StickerLibraryRow | null =
    library.find((row) => row.id === selectedStickerId) ?? null;
  const placeableLists = lists
    .map((list) => ({ list, cards: list.cards.filter(acceptsFrontSticker) }))
    .filter((entry) => entry.cards.length > 0);
  const refusal = !canPlace && disabledReason !== null ? disabledReason : undefined;
  /*
   * Three reasons in one boolean, and each one is a state the submit MUST NOT
   * be pressable in: nothing chosen (the mutation has no file), a chosen file
   * this board will refuse (the presign would 422 on bytes already read), and
   * a request in flight (a second `mutateAsync` would presign a second object
   * and confirm two library rows for one press).
   */
  const uploadDisabled =
    uploadFile === null || uploadError !== null || uploadSticker.isPending;

  function handleDragStart(event: DragEvent<HTMLDivElement>, stickerId: string): void {
    /*
     * The gate is repeated on the gesture and not left to `draggable={canPlace}`
     * alone: `draggable` is a rendering hint, and a browser that begins a drag
     * anyway would otherwise hand the drop target a payload the viewer is not
     * allowed to place, which the route would then refuse with a 403 the member
     * never asked for.
     */
    if (!canPlace) return;
    /*
     * ⚠ `effectAllowed` IS THE OTHER HALF OF THE MEDIA-TYPE CONTRACT, and the
     * half that fires no `drop` event at all when it is wrong. The HTML
     * drag-and-drop processing model forces the drag operation to `none` —
     * silently, with no `drop` — when the target's `dropEffect` is
     * incompatible with what the source allowed. `SortableCardTile` therefore
     * DERIVES its `dropEffect` from this value rather than asserting `'copy'`,
     * so any value but `'none'` lands; `'copy'` is the honest one, because a
     * placement copies a library row onto a card and removes nothing from the
     * tray. jsdom performs no negotiation whatsoever, so a mismatch here would
     * be a browser-only failure behind a green unit suite.
     */
    event.dataTransfer.effectAllowed = 'copy';
    event.dataTransfer.setData(STICKER_DRAG_MEDIA_TYPE, stickerId);
    event.dataTransfer.setData('text/plain', stickerId);
  }

  function selectUploadFile(event: ChangeEvent<HTMLInputElement>): void {
    const file = event.currentTarget.files?.[0] ?? null;
    setUploadFile(file);
    /*
     * Re-entering the picker and cancelling clears the file AND the sentence
     * about it — a refusal left standing beside no file would describe a
     * choice the member has already withdrawn.
     */
    setUploadError(file === null ? null : stickerUploadRefusal(t, file));
  }

  async function submitUpload(event: FormEvent<HTMLFormElement>): Promise<void> {
    /*
     * ⚠ `preventDefault` FIRST and unconditionally. A `<form>` with no handler
     * of its own navigates on Enter, and this one is mounted inside the board
     * menu panel — a full-page GET would close the panel, discard the board
     * face's caches and look exactly like a crash.
     */
    event.preventDefault();
    /*
     * ⚠ ALL THREE ARMS OF `uploadDisabled` ARE RE-ASKED HERE, and `isPending`
     * is the one that matters: `disabled` on the submit button does not disable
     * the FORM, and a submit event can arrive without that button being pressed
     * at all. A second `mutateAsync` while one is in flight would presign a
     * second object and confirm two library rows for one member's intent.
     */
    if (uploadFile === null || uploadSticker.isPending) return;
    const refused = stickerUploadRefusal(t, uploadFile);
    if (refused !== null) {
      setUploadError(refused);
      return;
    }

    setUploadError(null);
    try {
      const sticker = await uploadSticker.mutateAsync({
        file: uploadFile,
        name: uploadName,
      });
      /*
       * ⚠ ONE sink. See the file docstring: the card back pairs this key with a
       * visible `role="status"` paragraph and the catalogue records that as a
       * double announcement, so the tray takes the live region only. The
       * VISIBLE confirmation is the new tile in the grid above, written into
       * the library cache by the hook's own `onSuccess`.
       */
      announce(
        t('cardBack.stickers.upload.complete', { sticker: stickerLabel(t, sticker) }),
      );
      setUploadFile(null);
      setUploadName('');
      if (fileInputRef.current !== null) fileInputRef.current.value = '';
    } catch (cause) {
      /*
       * The thrown message is what the member reads — `putStickerBytes` and the
       * two guards all throw ROUTED `ApiError`s, and a 4xx from either leg
       * carries the server's own sentence. The key is the fallback for a
       * rejection that is not an `Error` at all.
       */
      setUploadError(
        cause instanceof Error
          ? cause.message
          : t('cardBack.stickers.error.uploadFailed'),
      );
    }
  }

  function place(card: BoardFaceCardSurface): void {
    if (selectedSticker === null || !canPlace) return;
    /*
     * ⚠ The clamp runs HERE, once, before the POST — an INPUT-side act and
     * never an output-side transform. `CardTile` renders `sticker.positionX`
     * verbatim and so does the card back, so `FR-CARD-032` AC4's *"another
     * member sees it in the same position"* stays a property of one number.
     * The keyboard default survives it unchanged (180 ≤ 276 − 32 and
     * 8 ≤ 107.2 − 32), which is the point: the clamp guarantees legality —
     * containment inside `CardTile`'s `overflow-hidden` box, a floor at 0, and
     * the four decimals `numeric(8,4)` stores — without moving a coordinate
     * anyone chose.
     */
    const { positionX, positionY } = clampFrontStickerPlacement({
      positionX: KEYBOARD_DROP_POINT.x,
      positionY: KEYBOARD_DROP_POINT.y,
      tileWidth: REFERENCE_TILE_WIDTH,
      tileHeight: REFERENCE_TILE_HEIGHT,
    });
    placement.mutate({
      cardId: card.id,
      stickerId: selectedSticker.id,
      stickerName: stickerLabel(t, selectedSticker),
      positionX,
      positionY,
      /*
       * ⚠ The stack is PER SURFACE, not per card — `CardStickerSection`'s
       * recorded rule. Seeding from the card's whole sticker count would leave
       * gaps that mean nothing, and two tray placements both sent at 0 would
       * degrade `FR-CARD-032`'s *"stacking order adjustable after placement"*
       * to insertion order.
       */
      zIndex: card.stickers.filter((sticker) => sticker.surface === 'front').length,
    });
  }

  return (
    <div className="flex flex-col gap-3">
      {stickers.error ? <Notice tone="bad" body={stickers.error.message} /> : null}
      {placement.error ? <Notice tone="bad" body={placement.error.message} /> : null}
      {refusal !== undefined ? <Notice tone="locked" body={refusal} /> : null}

      {stickers.isPending ? (
        <p className="text-sm text-ink-muted">{t('cardBack.stickers.loadingLibrary')}</p>
      ) : library.length === 0 ? (
        <p className="text-sm text-ink-muted">{t('cardBack.stickers.libraryEmpty')}</p>
      ) : (
        <div className="grid grid-cols-3 gap-2">
          {library.map((sticker) => {
            const label = stickerLabel(t, sticker);
            return (
              /*
               * ⚠ `draggable` on a WRAPPER, with the focusable control inside
               * it — `BoardFace.tsx`'s pinned-view shortcut exactly. A drag
               * begun on the button starts from its nearest draggable
               * ancestor, so one element serves the pointer gesture and another
               * serves the keyboard one, and neither has to pretend to be the
               * other.
               */
              <div
                key={sticker.id}
                draggable={canPlace}
                onDragStart={(event) => handleDragStart(event, sticker.id)}
                className={cn(
                  'rounded-card',
                  canPlace && 'cursor-grab active:cursor-grabbing',
                )}
              >
                <Button
                  type="button"
                  variant="toggle"
                  pressed={selectedStickerId === sticker.id}
                  disabled={!canPlace}
                  disabledReason={refusal}
                  className="h-20 w-full border border-border-strong p-2"
                  // The column lives on `Button`'s content span: `flex-col` on
                  // the button itself never reached the image and label.
                  contentClassName="flex min-w-0 max-w-full flex-col items-center gap-1"
                  onClick={() =>
                    setSelectedStickerId((previous) =>
                      previous === sticker.id ? null : sticker.id,
                    )
                  }
                >
                  <img
                    src={stickerImageSrc(sticker.renderUrl)}
                    alt=""
                    aria-hidden="true"
                    className="size-10 object-contain"
                  />
                  <span className="block max-w-full truncate text-xs">{label}</span>
                </Button>
              </div>
            );
          })}
        </div>
      )}

      {/*
        The keyboard half of `FR-EXP-027` AC1. It appears only once a sticker is
        chosen, so a resting tray is the library and nothing else — and it is
        omitted entirely when the board has no placeable card, rather than
        reaching for a catalogue key whose words describe something else (this
        catalogue's own docstrings are explicit that a key's NAME must match its
        job, and `cardBack.stickers.empty` means "no stickers on this card").
      */}
      {selectedSticker !== null && canPlace && placeableLists.length > 0 ? (
        <div className="flex flex-col gap-2 border-t border-border pt-3">
          {placeableLists.map(({ list, cards }) => (
            <div key={list.id} className="flex flex-col gap-1">
              <p className="text-xs font-medium text-ink-muted">{list.title}</p>
              {cards.map((card) => {
                const cardTitle =
                  card.title.trim() === '' ? t('cardBack.untitledCard') : card.title;
                return (
                  <Button
                    key={card.id}
                    type="button"
                    size="sm"
                    variant="quiet"
                    /*
                     * ⚠ `text-left` IS NOT REDUNDANT BESIDE `justify-start`,
                     * and the difference is invisible until a title WRAPS.
                     * `justify-start` positions the flex ITEM — `Button`
                     * renders its children in a `<span>` inside an
                     * `inline-flex` — and says nothing about `text-align`,
                     * while a `<button>`'s user-agent `text-align: center` is
                     * overridden by nothing in the base, the `quiet` variant,
                     * the `sm` size or this call site. One line is
                     * shrink-wrapped, so centring it looks identical to
                     * left-aligning it; the second line is where the two part,
                     * and a card list whose second lines float to the middle is
                     * unscannable. Measured and recorded by fan-out 12, patched
                     * here with the leaf test it said the patch would owe.
                     */
                    className="h-auto min-h-7 w-full justify-start py-1.5 text-left"
                    /*
                     * ⚠ ONE WHOLE MESSAGE WITH TWO SLOTS, never the visible
                     * title concatenated to a routed stem. Both slots must be
                     * supplied — `interfaceMessage` THROWS on a slot with no
                     * value — and the accessible name is what makes the
                     * keyboard path self-describing without a group label, so
                     * no new key is owed for one.
                     */
                    aria-label={t('cardBack.stickers.placeLabel', {
                      sticker: stickerLabel(t, selectedSticker),
                      title: cardTitle,
                    })}
                    disabled={placement.isPending}
                    onClick={() => place(card)}
                  >
                    {cardTitle}
                  </Button>
                );
              })}
            </div>
          ))}
        </div>
      ) : null}

      {/*
        `FR-CARD-032` AC3 — *"given the sticker tray is open, when a member
        uploads a custom image"*. OUTSIDE the library's three-armed ternary on
        purpose: the built-in pack has no rows anywhere, so the empty arm is the
        ordinary board, and it is the arm with no other path to a sticker.
      */}
      {canPlace ? (
        <form
          className="flex flex-col gap-2 border-t border-border pt-3"
          onSubmit={(event) => {
            void submitUpload(event);
          }}
        >
          <label htmlFor={fileInputId} className="text-sm font-medium text-ink">
            {t('cardBack.stickers.upload.fileLabel')}
          </label>
          {/*
            ⚠ A BARE `<input type="file">`, not a `TextField` and not a styled
            button over a hidden input: `accept` narrows the OS picker to the
            four types `STICKER_UPLOAD_CONTENT_TYPES` names, and the file-name
            readout the browser draws beside it is the only confirmation that a
            choice landed. The `file:` utilities style the browser's own button;
            its LABEL follows the browser language and no key in this workstream
            can reach it, which the catalogue records beside `upload.fileLabel`.
          */}
          <input
            ref={fileInputRef}
            id={fileInputId}
            type="file"
            accept={STICKER_UPLOAD_ACCEPT}
            className="block w-full rounded-card border border-border-strong bg-surface px-2 py-1.5 text-sm text-ink file:mr-2 file:rounded-card file:border-0 file:bg-accent file:px-2 file:py-1 file:text-sm file:font-medium file:text-accent-ink"
            onChange={selectUploadFile}
          />
          {/*
            Optional by the wire contract — `stickerConfirmBodySchema.name` is
            nullable, and an empty string is normalised to `null` inside
            `useUploadCustomSticker` rather than here, so both upload surfaces
            send one shape. `maxLength={255}` is the card back's, and it is the
            column width rather than a taste.
          */}
          <TextField
            label={t('cardBack.stickers.upload.nameLabel')}
            value={uploadName}
            onChange={(event) => setUploadName(event.currentTarget.value)}
            maxLength={255}
          />
          {uploadError !== null ? <Notice tone="bad" body={uploadError} /> : null}
          <Button
            type="submit"
            size="sm"
            variant="secondary"
            iconStart={<Upload className="size-4" />}
            busy={uploadSticker.isPending}
            busyLabel={t('cardBack.stickers.upload.busy')}
            disabled={uploadDisabled}
          >
            {t('cardBack.stickers.upload.submit')}
          </Button>
        </form>
      ) : null}
    </div>
  );
}
